import React, { useState } from 'react';
import LegalPage, { LEGAL_BRAND, LEGAL_CONTACT_EMAIL } from './LegalPage';

// ── SmsOptInPage ──────────────────────────────────────────────────────────────
// Public at /sms-opt-in: the opt-in form the carriers' 10DLC review verifies (Telnyx "10DLC Opt-in Form").
// Phone field; a SEPARATE, OPTIONAL SMS checkbox, unchecked by default; the full disclosure beside it; links to the
// Privacy Policy and Terms; a submit button that records consent in the OS (POST /api/communications/consent).
// The consent wording is the OS's version CONSENT_VERSION — change both together, never one alone.
const CONSENT_VERSION = '2026-10-02';

export default function SmsOptInPage() {
  const [phone, setPhone] = useState('');
  const [agree, setAgree] = useState(false);
  const [state, setState] = useState({ kind: 'idle' });

  async function submit(e) {
    e.preventDefault();
    setState({ kind: 'sending' });
    try {
      const res = await fetch('/api/communications/consent', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ phone, smsConsent: agree, consentVersion: CONSENT_VERSION }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.recorded) setState({ kind: 'done', text: `Thanks. You're opted in to text messages from ${LEGAL_BRAND}. Reply STOP at any time to opt out.` });
      else if (res.ok) setState({ kind: 'done', text: 'Thanks. Your number was received. You did not opt in to text messages, so we will not text you.' });
      else if (body.error === 'invalid_phone') setState({ kind: 'error', text: 'Please enter a valid US or Canada mobile number.' });
      else if (res.status === 429) setState({ kind: 'error', text: 'Too many attempts. Please wait a minute and try again.' });
      else setState({ kind: 'error', text: 'Something went wrong. Please try again.' });
    } catch {
      setState({ kind: 'error', text: 'Something went wrong. Please try again.' });
    }
  }

  return (
    <LegalPage title="Text message opt-in" effective="October 2, 2026" current="sms">
      <p>
        Sign up to receive text messages from {LEGAL_BRAND} about your account: account notifications, customer care
        replies and verification codes. Text messages are optional and are not required to use {LEGAL_BRAND}.
      </p>
      <form className="optin-form" onSubmit={submit} noValidate>
        <label className="optin-label" htmlFor="optin-phone">Mobile phone number</label>
        <input
          id="optin-phone"
          className="optin-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+1 (555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
        />
        <label className="optin-check">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>
            I agree to receive SMS account notifications, customer care messages and verification codes from {LEGAL_BRAND} at
            the phone number provided. Message frequency may vary. Standard message and data rates may apply. Reply STOP to opt
            out. Reply HELP for help. We will not share mobile information with third parties for promotional or marketing
            purposes.
          </span>
        </label>
        <div className="optin-links">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
        </div>
        <button className="optin-submit" type="submit" disabled={state.kind === 'sending' || !phone.trim()}>
          {state.kind === 'sending' ? 'Submitting…' : 'Submit'}
        </button>
        {state.kind === 'done' && <p className="optin-result" role="status">{state.text}</p>}
        {state.kind === 'error' && <p className="optin-result optin-error" role="alert">{state.text}</p>}
      </form>
      <p className="legal-meta">
        Questions? Email {LEGAL_CONTACT_EMAIL} or reply HELP to any message. Details of the messaging program are in
        our <a href="/terms#sms">Terms</a> and <a href="/privacy#sms">Privacy Policy</a>.
      </p>
    </LegalPage>
  );
}
